import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { cn } from '@/lib/utils';
// The scope union is the API contract, so it's owned by the data layer rather
// than redeclared here.
import type { ChatThreadScope } from '../services/data.store';
import { List, GitBranch, BarChart3, LayoutDashboard, Lightbulb, FileText, AppWindow, Gauge, Workflow, Sigma, User, Users, LucideIcon } from 'lucide-react';

export type ViewMode = 'table' | 'tree';
export type AnalyticsViewMode = 'charts' | 'dashboards';
export type ContextViewMode = 'analytics' | 'company';
export type UsageAnalyticsTab = 'embed-sessions' | 'ellie-usage';

interface ToggleOption<T extends string> {
  value: T;
  label: string;
  icon: LucideIcon;
}

interface GenericToggleProps<T extends string> {
  value: T;
  onChange: (mode: T) => void;
  options: ToggleOption<T>[];
}

export function GenericToggle<T extends string>({ value, onChange, options }: GenericToggleProps<T>) {
  return (
    <div className="flex items-center gap-0.5 border rounded-md p-1">
      {options.map((option) => (
        <Button
          key={option.value}
          variant="ghost"
          size="sm"
          onClick={() => onChange(option.value)}
          // Active tab is a darker grey surface rather than a primary fill.
          className={cn(
            'gap-2 h-8',
            value === option.value &&
              'bg-muted-strong text-muted-strong-foreground font-medium hover:bg-muted-strong hover:text-muted-strong-foreground'
          )}
        >
          <option.icon className="h-4 w-4" />
          {option.label}
        </Button>
      ))}
    </div>
  );
}

interface ViewModeToggleProps {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
}

export function ViewModeToggle({ value, onChange }: ViewModeToggleProps) {
  const options: [ToggleOption<ViewMode>, ToggleOption<ViewMode>] = [
    { value: 'table', label: 'Table', icon: List },
    { value: 'tree', label: 'Tree', icon: GitBranch },
  ];
  return <GenericToggle value={value} onChange={onChange} options={options} />;
}

interface AnalyticsViewToggleProps {
  value: AnalyticsViewMode;
  onChange: (mode: AnalyticsViewMode) => void;
}

export function AnalyticsViewToggle({ value, onChange }: AnalyticsViewToggleProps) {
  const options: [ToggleOption<AnalyticsViewMode>, ToggleOption<AnalyticsViewMode>] = [
    { value: 'charts', label: 'Charts', icon: BarChart3 },
    { value: 'dashboards', label: 'Dashboards', icon: LayoutDashboard },
  ];
  return <GenericToggle value={value} onChange={onChange} options={options} />;
}

interface ContextViewToggleProps {
  value: ContextViewMode;
  onChange: (mode: ContextViewMode) => void;
}

export function ContextViewToggle({ value, onChange }: ContextViewToggleProps) {
  const options: [ToggleOption<ContextViewMode>, ToggleOption<ContextViewMode>] = [
    { value: 'analytics', label: 'Analytics Context', icon: Lightbulb },
    { value: 'company', label: 'Company Context', icon: FileText },
  ];
  return <GenericToggle value={value} onChange={onChange} options={options} />;
}

interface ChatScopeToggleProps {
  value: ChatThreadScope;
  onChange: (scope: ChatThreadScope) => void;
}

const CHAT_SCOPE_OPTIONS: [ToggleOption<ChatThreadScope>, ToggleOption<ChatThreadScope>] = [
  { value: 'mine', label: 'Your Chats', icon: User },
  { value: 'all', label: 'All Chats', icon: Users },
];

/**
 * Whose conversations the chat history lists. Only offered to Admins — everyone
 * else can only ever see their own, so there is nothing to switch between.
 *
 * Wears GenericToggle's look but splits the full width between its two options
 * instead of sizing to its labels: this one lives in the chat history column,
 * which is a quarter of the page, and a content-sized toggle overflows it on a
 * laptop screen.
 */
export function ChatScopeToggle({ value, onChange }: ChatScopeToggleProps) {
  return (
    <div className="flex items-center gap-0.5 border rounded-md p-1 w-full">
      {CHAT_SCOPE_OPTIONS.map((option) => (
        <Button
          key={option.value}
          variant="ghost"
          size="sm"
          onClick={() => onChange(option.value)}
          className={cn(
            'flex-1 min-w-0 gap-1.5 h-7 px-2 text-xs',
            value === option.value &&
              'bg-muted-strong text-muted-strong-foreground font-medium hover:bg-muted-strong hover:text-muted-strong-foreground'
          )}
        >
          <option.icon className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">{option.label}</span>
        </Button>
      ))}
    </div>
  );
}

/**
 * What the diagram draws on top of the entity graph. Entities and the relationships
 * between them are always drawn — these are the layers stacked over them.
 */
export interface DiagramLayers {
  metrics: boolean;
  sources: boolean;
}

/** Entities and their relationships alone. */
export const DEFAULT_DIAGRAM_LAYERS: DiagramLayers = { metrics: false, sources: false };

interface DiagramLayerTogglesProps {
  value: DiagramLayers;
  onChange: (layers: DiagramLayers) => void;
}

const DIAGRAM_LAYER_OPTIONS: { key: keyof DiagramLayers; label: string; icon: LucideIcon }[] = [
  { key: 'metrics', label: 'Metrics', icon: Sigma },
  { key: 'sources', label: 'Sources & Transformations', icon: Workflow },
];

export function DiagramLayerToggles({ value, onChange }: DiagramLayerTogglesProps) {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border rounded-md px-3 py-2">
      {DIAGRAM_LAYER_OPTIONS.map((option) => (
        <div key={option.key} className="flex items-center gap-2">
          <Switch
            id={`diagram-layer-${option.key}`}
            checked={value[option.key]}
            onCheckedChange={(checked) => onChange({ ...value, [option.key]: checked })}
          />
          <Label
            htmlFor={`diagram-layer-${option.key}`}
            className="flex items-center gap-1.5 text-sm font-normal cursor-pointer"
          >
            <option.icon className="h-4 w-4" />
            {option.label}
          </Label>
        </div>
      ))}
    </div>
  );
}

export const USAGE_ANALYTICS_TABS: ToggleOption<UsageAnalyticsTab>[] = [
  { value: 'embed-sessions', label: 'Embed Sessions', icon: AppWindow },
  { value: 'ellie-usage', label: 'Ellie Usage', icon: Gauge },
];

export function usageAnalyticsTabLabel(tab: UsageAnalyticsTab): string {
  return USAGE_ANALYTICS_TABS.find((t) => t.value === tab)?.label ?? '';
}

interface UsageAnalyticsTabsProps {
  value: UsageAnalyticsTab;
  onChange: (tab: UsageAnalyticsTab) => void;
  /** Tabs to show; defaults to all of them. */
  options?: ToggleOption<UsageAnalyticsTab>[];
}

export function UsageAnalyticsTabs({ value, onChange, options = USAGE_ANALYTICS_TABS }: UsageAnalyticsTabsProps) {
  return <GenericToggle value={value} onChange={onChange} options={options} />;
}
